import { HttpClient } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { EnvironmentToken } from '../../app.config';
import {
  Defect,
  PsaOption,
  RouteWithSections,
  UserOption,
} from '../models/defect.model';

export class DefectService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  #isLoading = signal<Set<unknown>>(new Set());

  #defects = new BehaviorSubject<Defect[]>([]);

  public defects$ = this.#defects.asObservable();
  public defects = toSignal(this.defects$, {
    initialValue: [],
  });

  #routeOptions = new BehaviorSubject<RouteWithSections[]>([]);
  public routeOptions$ = this.#routeOptions.asObservable();
  public routeOptions = toSignal(this.routeOptions$, { initialValue: [] });

  #psaOptions = new BehaviorSubject<PsaOption[]>([]);
  public psaOptions$ = this.#psaOptions.asObservable();
  public psaOptions = toSignal(this.psaOptions$, { initialValue: [] });

  #responsibleUserOptions = new BehaviorSubject<UserOption[]>([]);
  public responsibleUserOptions$ = this.#responsibleUserOptions.asObservable();
  public responsibleUserOptions = toSignal(this.responsibleUserOptions$, {
    initialValue: [],
  });

  getDefects() {
    this.#execute(
      this.#http.get<{ data: Defect[] }>(`${this.#apiUrl}/api/defects`),
    ).subscribe({
      next: (response: any) => {
        const defects: Defect[] = (response.data ?? response).map(
          (d: Defect) => ({
            ...d,
            responsible_user_name: d.responsible_user?.name ?? null,
          }),
        );
        this.#defects.next(defects);
      },
      error: (e: any) => console.log(e),
    });
  }

  getDefect(id: number) {
    return this.#execute(
      this.#http.get<{ data: Defect }>(`${this.#apiUrl}/api/defects/${id}`),
    );
  }

  createDefect(formData: FormData) {
    return this.#execute(
      this.#http.post<{ data: Defect }>(
        `${this.#apiUrl}/api/defects`,
        formData,
      ),
    );
  }

  updateDefect(formData: FormData, id: number) {
    return this.#execute(
      this.#http.post<{ data: Defect }>(
        `${this.#apiUrl}/api/defects/${id}`,
        formData,
      ),
    );
  }

  deleteDefect(id: number) {
    return this.#execute(
      this.#http.delete(`${this.#apiUrl}/api/defects/${id}`),
    );
  }

  deleteMedia(defectId: number, mediaId: number) {
    return this.#execute(
      this.#http.delete(
        `${this.#apiUrl}/api/defects/${defectId}/media/${mediaId}`,
      ),
    );
  }

  addComment(defectId: number, comment: string) {
    return this.#execute(
      this.#http.post<{ data: Defect }>(
        `${this.#apiUrl}/api/defects/${defectId}/comments`,
        { comment },
      ),
    );
  }

  deleteComment(defectId: number, commentId: number) {
    return this.#execute(
      this.#http.delete(
        `${this.#apiUrl}/api/defects/${defectId}/comments/${commentId}`,
      ),
    );
  }

  getRouteOptions() {
    this.#execute(
      this.#http.get<RouteWithSections[]>(
        `${this.#apiUrl}/api/inspections/items?type=route_section`,
      ),
    ).subscribe({
      next: (options: any) => {
        this.#routeOptions.next(options);
      },
      error: (e: any) => console.log(e),
    });
  }

  getPsaOptions() {
    this.#execute(
      this.#http.get<PsaOption[]>(
        `${this.#apiUrl}/api/inspections/items?type=psa`,
      ),
    ).subscribe({
      next: (options: any) => {
        this.#psaOptions.next(options);
      },
      error: (e: any) => console.log(e),
    });
  }

  getResponsibleUsers() {
    this.#execute(
      this.#http.get<{ data: UserOption[] }>(
        `${this.#apiUrl}/api/defects/responsible-users`,
      ),
    ).subscribe({
      next: (response: any) => {
        this.#responsibleUserOptions.next(response.data ?? response);
      },
      error: (e: any) => console.log(e),
    });
  }

  #execute<T = unknown>(apiCall: Observable<T>) {
    this.#isLoading.update((x) => {
      x.add(apiCall);
      return new Set(x.values());
    });
    return apiCall.pipe(
      finalize(() =>
        this.#isLoading.update((x) => {
          x.delete(apiCall);
          return new Set(x.values());
        }),
      ),
    );
  }
}
